import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { eur } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Mail, Send, Gauge, Target, ShieldCheck, Handshake, AlertTriangle, CalendarClock, Radar, Route } from "lucide-react";
import { toast } from "sonner";
import AgencyScorecard from "@/components/AgencyScorecard";
import { PianoPresidioDialog } from "@/pages/ObiettiviAgenzie";

const MGMT = ["admin", "back_office", "commercial_manager", "head_of_partner"];

const LEVEL_CHIP = { ok: "bg-emerald-500/15 text-emerald-400", warn: "bg-amber-500/15 text-amber-400", bad: "bg-red-500/15 text-red-400" };
const LEVEL_LABEL = { ok: "in linea", warn: "quasi in linea", bad: "sotto target" };
const Chip = ({ ok, level }) => {
  const lv = level !== undefined ? level : (ok === null || ok === undefined ? null : ok ? "ok" : "bad");
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${lv ? LEVEL_CHIP[lv] : "bg-muted text-muted-foreground"}`}>{lv ? LEVEL_LABEL[lv] : "N/D"}</span>;
};
const Tile = ({ icon: Icon, label, value, ok, level, hint, testid }) => (
  <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {label}</span>
      {(ok !== undefined || level !== undefined) && <Chip ok={ok} level={level} />}
    </div>
    <div className="mt-1 font-heading text-2xl font-black leading-tight">{value}</div>
    {hint && <div className="mt-0.5 text-[10px] leading-tight text-muted-foreground">{hint}</div>}
  </div>
);

export default function ValutazioneOverview({ sel, setSel, agencies = [] }) {
  const { user } = useAuth();
  const [ov, setOv] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("aggregate");
  const [sender, setSender] = useState("back_office");
  const [ccRoles, setCcRoles] = useState({ back_office: false, direzione: false, responsabile: false });
  const [toRoles, setToRoles] = useState({ back_office: false, direzione: true, responsabile: false });
  const [toFree, setToFree] = useState("");
  const [ccFree, setCcFree] = useState("");
  const [pianoOpen, setPianoOpen] = useState(false);

  useEffect(() => {
    // all'apertura di un'agenzia specifica mostra automaticamente il Piano di presidio
    setPianoOpen(!!sel && sel !== "all");
  }, [sel]);

  useEffect(() => {
    if (sel === "all") {
      setOv(null);
      api.get("/agency-valutazione/overview?agency_id=all").then((r) => setOv(r.data)).catch(() => setOv(false));
    }
  }, [sel]);

  const [rmails, setRmails] = useState({});
  useEffect(() => { api.get("/email-recipients/roles").then((r) => setRmails(r.data || {})).catch(() => {}); }, []);
  const em = (k) => (rmails[k] && rmails[k].length ? ` (${rmails[k].join(", ")})` : "");

  if (!MGMT.includes(user?.role)) return null;

  const doSend = async () => {
    setSending(true);
    try {
      const body = {
        agency_id: sel, sender,
        cc_roles: Object.keys(ccRoles).filter((k) => ccRoles[k]),
        cc_emails: ccFree.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => s.includes("@")),
      };
      if (sel === "all") {
        body.mode = mode;
        if (mode === "aggregate") {
          body.to_roles = Object.keys(toRoles).filter((k) => toRoles[k]);
          body.to_emails = toFree.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => s.includes("@"));
        }
      }
      const r = await api.post("/agency-valutazione/send", body);
      toast.success(r.data.mode === "per_agency" ? `Inviate ${r.data.agenzie} email alle agenzie` : `Email inviata a ${r.data.inviate} destinatario/i`);
      setSendOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito"); } finally { setSending(false); }
  };

  return (
    <Card className="p-5" data-testid="valutazione-overview">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Gauge className="h-5 w-5 text-primary" />
          <div>
            <h2 className="font-heading text-lg font-black tracking-tight">Valutazione</h2>
            <p className="text-[11px] text-muted-foreground">Giudizio della Direzione (con AI) per agenzia · riepilogo KPI per la rete</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="h-9 w-[240px]" data-testid="valutazione-ov-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-[400px]">
              <SelectItem value="all">Tutte le agenzie</SelectItem>
              {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {sel !== "all" && (
            <Button variant="outline" size="sm" className="h-9" onClick={() => setPianoOpen(true)} data-testid="valutazione-piano-btn">
              <Route className="mr-2 h-4 w-4" /> Piano di presidio
            </Button>
          )}
        </div>
      </div>

      {sel !== "all" ? (
        <>
          <AgencyScorecard agencyId={sel} />
          {pianoOpen && <PianoPresidioDialog agencyId={sel} agencyName={agencies.find((a) => a.id === sel)?.name} onClose={() => setPianoOpen(false)} />}
        </>
      ) : ov === null ? (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carico i KPI di rete…</div>
      ) : ov ? (() => {
        const rows = ov.agencies || [];
        const a = rows.reduce((s, r) => ({
          fatturato: s.fatturato + (r.fatturato || 0),
          impegno: s.impegno + (r.impegno_vendita || 0),
          acs: s.acs + (r.azioni_clienti_svolte || 0), act: s.act + (r.azioni_clienti_target || 0),
          ps: s.ps + (r.prospecting_svolte || 0), pp: s.pp + (r.prospecting_previste || 0),
          p2: s.p2 + (r.pianificate_2w || 0), p2t: s.p2t + (r.pianificate_2w_target || 0),
          conv: s.conv + (r.conversioni || 0), risk: s.risk + (r.clienti_a_rischio || 0),
        }), { fatturato: 0, impegno: 0, acs: 0, act: 0, ps: 0, pp: 0, p2: 0, p2t: 0, conv: 0, risk: 0 });
        const pctVen = a.impegno > 0 ? Math.round(a.fatturato / a.impegno * 100) : null;
        const acPct = a.act > 0 ? Math.round(a.acs / a.act * 100) : null;
        const acLevel = acPct == null ? null : (acPct >= 100 ? "ok" : acPct >= 70 ? "warn" : "bad");
        const psPct = a.pp > 0 ? Math.round(a.ps / a.pp * 100) : null;
        const p2Pct = a.p2t > 0 ? Math.round(a.p2 / a.p2t * 100) : null;
        const p2Level = p2Pct == null ? null : (p2Pct > 70 ? "ok" : p2Pct > 50 ? "warn" : "bad");
        return (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="valutazione-ov-aggregate">
            <Tile testid="valov-vendite" icon={Target} label="Obiettivo vendite" value={pctVen != null ? `${pctVen}%` : "—"} ok={pctVen == null ? null : pctVen >= 100} hint={`${eur(a.fatturato)} / ${eur(a.impegno)} (settimana)`} />
            <Tile testid="valov-clienti" icon={ShieldCheck} label="Azioni su clienti" value={`${a.acs}/${a.act}`} level={acLevel} hint={`azioni su clienti A/B/C · rete${acPct != null ? ` · ${acPct}%` : ""}`} />
            <Tile testid="valov-conversioni" icon={Handshake} label="Partner convertiti" value={a.conv} ok={a.conv >= 1} hint="ultimi 30 giorni · rete" />
            <Tile testid="valov-prospect" icon={Radar} label="Prospect lavorati" value={`${a.ps}/${a.pp}`} ok={a.pp ? a.ps >= a.pp : null} hint={`prospect distinti lavorati · rete${psPct != null ? ` · ${psPct}%` : ""}`} />
            <Tile testid="valov-pianificate" icon={CalendarClock} label="Pianificate 2 sett." value={a.p2} level={p2Level} hint={`prossime 2 settimane · obiettivo ${a.p2t}${p2Pct != null ? ` · ${p2Pct}%` : ""}`} />
            <Tile testid="valov-rischio" icon={AlertTriangle} label="Clienti a rischio" value={a.risk} ok={a.risk === 0} hint="alto valore in calo/inattivi · rete" />
          </div>
        );
      })() : <p className="py-4 text-sm text-muted-foreground">Dati non disponibili.</p>}

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md" data-testid="valutazione-ov-send">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /> Invia valutazione via email</DialogTitle>
            <DialogDescription>{sel === "all" ? "Selezione: tutte le agenzie (solo riepilogo KPI, senza AI)" : "Selezione: agenzia singola (valutazione completa con AI agli agenti)"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border border-primary/30 bg-primary/[0.06] p-3 text-[12px] leading-snug text-muted-foreground" data-testid="valutazione-send-note">
              {sel !== "all"
                ? "L'email contiene la valutazione completa della Direzione (giudizio, trend, performance e analisi AI) e viene inviata agli agenti dell'agenzia selezionata."
                : mode === "per_agency"
                  ? "Invio massivo: un'email per ciascuna agenzia con il solo riepilogo KPI (senza AI), inviata ai rispettivi agenti."
                  : "Email unica aggregata: un solo messaggio con il riepilogo KPI di tutte le agenzie (senza AI), inviato ai destinatari indicati in \u201cA\u201d."}
              {" "}Il mittente (Da) definisce firma e indirizzo di risposta; i contatti in CC ricevono copia per conoscenza.
            </div>
            {sel === "all" && (
              <div>
                <Label className="text-[11px] font-bold uppercase text-muted-foreground">Modalità</Label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger className="mt-1.5 h-9" data-testid="valutazione-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aggregate">Email unica aggregata (a Direzione/BO/indirizzi)</SelectItem>
                    <SelectItem value="per_agency">Invio massivo: a ogni agenzia i propri KPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[11px] font-bold uppercase text-muted-foreground">Mittente (Da)</Label>
              <Select value={sender} onValueChange={setSender}>
                <SelectTrigger className="mt-1.5 h-9" data-testid="valutazione-sender"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="back_office">Back Office{em("back_office")}</SelectItem>
                  <SelectItem value="direzione">Direzione{em("direzione")}</SelectItem>
                  <SelectItem value="responsabile">Responsabile Commerciale{em("responsabile")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {sel === "all" && mode === "aggregate" && (
              <div>
                <Label className="text-[11px] font-bold uppercase text-muted-foreground">Destinatari (A)</Label>
                <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-2">
                  <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={toRoles.direzione} onCheckedChange={(v) => setToRoles((s) => ({ ...s, direzione: !!v }))} data-testid="valutazione-to-dir" /> Direzione{em("direzione")}</label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={toRoles.back_office} onCheckedChange={(v) => setToRoles((s) => ({ ...s, back_office: !!v }))} data-testid="valutazione-to-bo" /> Back Office{em("back_office")}</label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={toRoles.responsabile} onCheckedChange={(v) => setToRoles((s) => ({ ...s, responsabile: !!v }))} data-testid="valutazione-to-resp" /> Responsabile{em("responsabile")}</label>
                </div>
                <Input value={toFree} onChange={(e) => setToFree(e.target.value)} placeholder="Email destinatari (separate da virgola)" className="mt-2 h-9" data-testid="valutazione-to-free" />
              </div>
            )}
            <div>
              <Label className="text-[11px] font-bold uppercase text-muted-foreground">In copia (CC)</Label>
              <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-2">
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={ccRoles.back_office} onCheckedChange={(v) => setCcRoles((s) => ({ ...s, back_office: !!v }))} /> Back Office{em("back_office")}</label>
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={ccRoles.direzione} onCheckedChange={(v) => setCcRoles((s) => ({ ...s, direzione: !!v }))} /> Direzione{em("direzione")}</label>
                <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={ccRoles.responsabile} onCheckedChange={(v) => setCcRoles((s) => ({ ...s, responsabile: !!v }))} /> Responsabile{em("responsabile")}</label>
              </div>
              <Input value={ccFree} onChange={(e) => setCcFree(e.target.value)} placeholder="Email in copia (separate da virgola)" className="mt-2 h-9" data-testid="valutazione-cc-free" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Annulla</Button>
            <Button onClick={doSend} disabled={sending} data-testid="valutazione-send-submit">{sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />} Invia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
